import styled from '@emotion/styled';
import { Modal, Radio, Button } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import useSystem, { SystemLanguage } from 'hooks/business/system/useSystem';
import languageOptions from 'components/SystemLangs/languageOptions';
import { useSetAutoTranslateConfig } from 'hooks/business/im/chatPreference';
import { isQuickChat } from 'productLine';
import { useSetReceptionConfig } from 'views/Setting/ConversationSetting/useReceptionConfig';
import useLayerLock from 'hooks/useLayerLock';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';

const HAS_INITIALIZED_LANG_STORAGE_KEY = 'fett/langInitialized';

const isNeedAutoTranslateLang = (lang: General.SystemLanguage) => {
  if (isQuickChat) {
    return false;
  }

  const noNeedAutoTranslateLangs: General.SystemLanguage[] = ['id', 'th', 'tl', 'vi'];

  const needAutoTranslate = !noNeedAutoTranslateLangs.includes(lang);

  return needAutoTranslate;
};

const RadioContainer = styled.div`
  margin-top: 26px;
  margin-left: 40px;
`;
const ButtonContainer = styled.div`
  margin-top: 57px;
  text-align: center;
`;
const InitLang = React.memo(() => {
  const { handleLanguageSettingChange } = useSystem();
  const [visible, setVisible] = useState(
    () => localStorage.getItem(HAS_INITIALIZED_LANG_STORAGE_KEY) === null,
  );
  const [lang, setLang] = useState<SystemLanguage>('zh-CN');
  const onChange = useCallback((e) => {
    setLang(e.target.value);
  }, []);
  const { gained, release, acquire } = useLayerLock('initLang', 99999);
  useEffect(() => {
    if (!visible) {
      release();
      return () => {};
    }
    acquire();
    return () => {
      release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const setAutoTranslateConfig = useSetAutoTranslateConfig();

  const setReceptionConfig = useSetReceptionConfig();

  const {
    data: userConfig,
    error: userConfigError,
    refetch: refetchUserConfig,
  } = useQuery({
    ...queries.accounts.userConfig,
    enabled: visible,
  });

  const updateUserConfig = useCallback(
    (config: {
      qaAutoTranslate: boolean;
      isFirstLogin: boolean;
      receptionConfig: General.ReceptionConfig;
    }) => {
      const { qaAutoTranslate, isFirstLogin, receptionConfig } = config;
      setReceptionConfig(receptionConfig);

      if (isFirstLogin) {
        const needAutoTranslate = isNeedAutoTranslateLang(lang);

        setAutoTranslateConfig(needAutoTranslate);
      } else {
        setAutoTranslateConfig(qaAutoTranslate, false);
      }
    },
    [setReceptionConfig, lang, setAutoTranslateConfig],
  );

  const onSave = useCallback(() => {
    setVisible(false);
    handleLanguageSettingChange(lang);
    localStorage.setItem(HAS_INITIALIZED_LANG_STORAGE_KEY, 'initialized');
    refetchUserConfig();
  }, [handleLanguageSettingChange, lang, refetchUserConfig]);

  useEffect(() => {
    if (userConfig) {
      updateUserConfig(userConfig);
    }
  }, [updateUserConfig, userConfig]);

  useEffect(() => {
    if (userConfigError) {
      setReceptionConfig(undefined);
    }
  }, [userConfigError, setReceptionConfig]);

  return (
    <Modal
      zIndex={99999}
      title="系统语言设置"
      visible={visible && gained}
      onCancel={onSave}
      footer={null}
    >
      <div>
        <Radio.Group onChange={onChange} value={lang}>
          {languageOptions.map(({ value, label }) => {
            return (
              <RadioContainer key={value}>
                <Radio value={value}>{label}</Radio>
              </RadioContainer>
            );
          })}
        </Radio.Group>
      </div>
      <ButtonContainer>
        <Button onClick={onSave} type="primary">
          确认设置
        </Button>
      </ButtonContainer>
    </Modal>
  );
});
InitLang.displayName = 'InitLang';

export default InitLang;
